import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { AiOutlineFileAdd } from "react-icons/ai";
import { BsPencilFill } from "react-icons/bs";
import { LuFilter, LuX } from 'react-icons/lu';
import { GrAttachment } from "react-icons/gr";
import { FaBox, FaBoxOpen, FaRegHandPaper, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoSettingsOutline } from "react-icons/io5";
import { FiDownload } from "react-icons/fi";
import axios from 'axios';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import { useCurrency } from '../../../../context/currencyContext';
import usePricingAccess from '../../../../hooks/usePricingAccess.js';
import Swal from 'sweetalert2';
import './OpenCases.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com';

// =====================================================
// GET USER LAB ID
// =====================================================
const getUserLabId = () => {
  try {
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          const userLabId =
            parsed.userLabId ||
            parsed.labId ||
            parsed.id ||
            (parsed.data && parsed.data.userLabId) ||
            (parsed.data && parsed.data.labId) ||
            (parsed.user && parsed.user.userLabId) ||
            (parsed.user && parsed.user.labId);

          if (userLabId) return userLabId;

          if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
            return storedData;
          }
        } catch (e) {
          continue;
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const value = localStorage.getItem(localStorage.key(i));
      if (value && value.match(/^000000\d{2}$/)) {
        return value;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

// Short display date like "1 Oct 2026"
const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// =====================================================
// CASE STATUS ICON HELPERS (same as Cases Opened)
//   on hold -> hand, invoiced -> closed box, open / in progress -> open box
// =====================================================
const isCaseOnHold = (c) => /hold/i.test(String(c.status || c.caseStatus || ''));

const getStatusIcon = (c) => {
  if (isCaseOnHold(c)) {
    return { Icon: FaRegHandPaper, className: 'icon-inbox icon-hold', tip: c.status || 'On Hold' };
  }
  if (c.invoiceNumber) {
    return { Icon: FaBox, className: 'icon-inbox icon-closed-box', tip: 'Closed - Invoiced' };
  }
  return { Icon: FaBoxOpen, className: 'icon-inbox icon-open-box', tip: c.status || 'Open' };
};

// =====================================================
// COLUMN SORT HELPERS (same as Cases Opened)
// =====================================================
const getSortValue = (c, key) => {
  switch (key) {
    case 'status':
      return isCaseOnHold(c) ? 2 : c.invoiceNumber ? 1 : 0;
    case 'caseNumber':
      return Number(c.caseNumber) || String(c.caseNumber || '');
    case 'invoice':
      return Number(c.invoiceNumber) || '';
    case 'account':
      return c.account_name || c.account_holder || '';
    case 'provider':
      return c.provider_name || '';
    case 'patient':
      return c.last_name || '';
    case 'accountShip':
      return c.start_date ? new Date(c.start_date).getTime() : '';
    case 'labShip':
      return c.arrive_date ? new Date(c.arrive_date).getTime() : '';
    case 'delivery':
      return c.delivery_date ? new Date(c.delivery_date).getTime() : '';
    default:
      return '';
  }
};

const sortByColumn = (list, sortConfig) => {
  if (!sortConfig.key) return list;

  const dir = sortConfig.dir === 'desc' ? -1 : 1;

  return [...list].sort((a, b) => {
    const va = getSortValue(a, sortConfig.key);
    const vb = getSortValue(b, sortConfig.key);

    if (va === '' && vb === '') return 0;
    if (va === '') return 1;
    if (vb === '') return -1;

    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }

    return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
  });
};

const SortHeader = ({ label, colKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === colKey;

  return (
    <th className="sortable-th" onClick={() => onSort(colKey)}>
      {label}
      <span className="sort-arrows">
        <FaSortUp className={`sort-icon${isActive && sortConfig.dir === 'asc' ? ' active' : ''}`} />
        <FaSortDown className={`sort-icon${isActive && sortConfig.dir === 'desc' ? ' active' : ''}`} />
      </span>
    </th>
  );
};

// =====================================================
// SWEET ALERT HELPERS
// =====================================================
const showSuccessAlert = (title, message) => {
  Swal.fire({
    title, text: message, icon: 'success', toast: true,
    position: 'top-end', showConfirmButton: false, timer: 3000,
    timerProgressBar: true, background: '#ffffff', iconColor: '#004aad'
  });
};

const showErrorAlert = (title, message) => {
  Swal.fire({
    title, text: message, icon: 'error', toast: true,
    position: 'top-end', showConfirmButton: false, timer: 4000,
    timerProgressBar: true
  });
};

// =====================================================
// MAIN COMPONENT
// =====================================================
const OpenCases = () => {
  const { formatAmount } = useCurrency();

  // Pricing access — hides amounts if user doesn't have access
  const { hasPricingAccess, loading: pricingLoading } = usePricingAccess();

  // Main data states
  const [cases, setCases] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // ✅ PAGINATION: 5 cases per page (change this number to use a different page size)
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Column sort (click header arrows)
  const [sortConfig, setSortConfig] = useState({ key: '', dir: 'asc' });

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, accountName } = location.state || {};

  const dropdownRef = useRef({});
  const isInitialMount = useRef(true);
  // Used to ignore stale responses when requests overlap
  const requestIdRef = useRef(0);

  // Filter controls
  const [filters, setFilters] = useState({
    filterType: 'account',
    filterValue: '',
    by: 'month',
    caseTag: 'any',
    properties: 'all',
    startDate: '',
    endDate: '',
    caseStatus: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    accounts: [], providers: [], patients: [], caseIds: [], deliveryDates: [], caseTags: [], statuses: []
  });

  // Format helpers
  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, '0') : 'N/A';
  };

  // Returns null when user has no pricing access.
  const formatAmountWithCommas = (amount) => {
    if (!hasPricingAccess) return null;
    if (typeof amount !== 'number' || isNaN(amount)) return null;
    const formatted = formatAmount(amount);
    if (typeof formatted !== 'string') return formatted;
    return formatted.replace(/\d+(\.\d+)?/g, (match) => {
      const parts = match.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    });
  };

  // =====================================================
  // OPEN CASES = cases WITHOUT an invoiceNumber
  // The server already filters this (statusFilter: 'open'); this is a
  // safety net so an invoiced case can never appear on this page.
  // =====================================================
  const onlyNonInvoicedCases = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    return list.filter((c) => {
      const inv = c?.invoiceNumber;
      return inv === null || inv === undefined || String(inv).trim() === '';
    });
  }, []);

  // =====================================================
  // FETCH FILTER OPTIONS
  // =====================================================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) return;

      const response = await axios.get(`${API_URL}/api/cases/filter-options`, {
        params: {
          userLabId,
          ...(accountId && { accountId }),
          statusFilter: 'open'
        }
      });

      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, [accountId]);

  // =====================================================
  // FETCH FILTERED CASES FROM BACKEND (server-side pagination)
  // Only `itemsPerPage` cases are returned per request.
  // =====================================================
  const fetchFilteredCases = useCallback(async (currentFilters, page = 1) => {
    const requestId = ++requestIdRef.current;

    try {
      setFilterLoading(true);
      setError(null);

      const userLabId = getUserLabId();
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        return;
      }

      const now = new Date();

      const params = {
        userLabId,
        statusFilter: 'open',
        page,
        limit: itemsPerPage,
        filterType: currentFilters.filterType,
        filterValue: currentFilters.filterValue,
        by: currentFilters.by,
        caseTag: currentFilters.caseTag,
        properties: currentFilters.properties,
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
        caseStatus: currentFilters.caseStatus,
        currentMonth: now.getMonth(),
        currentYear: now.getFullYear(),
        ...(accountId && { accountId })
      };

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        params
      });

      // A newer request has started — ignore this (stale) response
      if (requestId !== requestIdRef.current) return;

      if (response.data.success) {
        // ✅ Open Cases = only cases with NO invoiceNumber yet
        const casesData = onlyNonInvoicedCases(response.data.data || []);

        // ---- read pagination info from any common response shape ----
        const body = response.data;
        const meta = body.pagination || body.meta || {};

        const rawCount =
          body.totalCount ?? body.total ?? body.totalItems ?? body.totalCases ??
          meta.totalCount ?? meta.total ?? meta.totalItems ?? meta.count;
        const parsedCount = Number(rawCount);
        const hasCount = rawCount !== undefined && rawCount !== null && !isNaN(parsedCount);

        const rawPages =
          body.totalPages ?? body.pages ?? body.pageCount ??
          meta.totalPages ?? meta.pages ?? meta.pageCount;
        const parsedPages = Number(rawPages);
        const hasPages = rawPages !== undefined && rawPages !== null && !isNaN(parsedPages) && parsedPages > 0;

        let resolvedPages;
        if (hasPages) {
          resolvedPages = parsedPages;
        } else if (hasCount) {
          resolvedPages = Math.max(Math.ceil(parsedCount / itemsPerPage), 1);
        } else {
          resolvedPages = 1;
        }

        // A full page means there may be more; never let a low backend value hide it
        const minCount = (page - 1) * itemsPerPage + casesData.length;
        const inferredPages = casesData.length >= itemsPerPage ? page + 1 : page;
        const finalPages = Math.max(resolvedPages, inferredPages);

        // Empty page beyond the first -> step back
        if (casesData.length === 0 && page > 1) {
          setTotalPages(page - 1);
          setCurrentPage(page - 1);
          return;
        }

        setCases(casesData);
        setTotalPages(finalPages);
        setTotalCount(hasCount ? Math.max(parsedCount, minCount) : minCount);

        // Total for ALL matching cases (server computed); else this page's sum
        const pageSum = casesData.reduce((sum, c) => sum + (c.totalPrice || 0), 0);
        setMonthlyTotal(typeof body.totalAmount === 'number' ? body.totalAmount : pageSum);
      } else {
        setError(response.data.message || 'Failed to load cases.');
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('Error fetching cases:', error);
      setError(error.response?.data?.message || 'Failed to load cases. Please try again later.');
    } finally {
      if (requestId === requestIdRef.current) {
        setFilterLoading(false);
        setLoading(false);
      }
    }
  }, [accountId, itemsPerPage, onlyNonInvoicedCases]);

  // =====================================================
  // FETCH ACCOUNTS
  // =====================================================
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const userLabId = getUserLabId();
        if (!userLabId) {
          setAccounts([]);
          return;
        }
        const response = await axios.get(`${API_URL}/api/account/lab-accounts/${userLabId}`);
        const formattedAccounts = (response.data.data || []).map(account => ({
          _id: account._id,
          name: account.account_name,
          accountId: account.accountId,
        }));
        setAccounts(formattedAccounts);
      } catch (error) {
        setAccounts([]);
      }
    };
    fetchAccounts();
  }, []);

  // =====================================================
  // INITIAL FETCH
  // =====================================================
  useEffect(() => {
    fetchFilterOptions();
    fetchFilteredCases(filters, 1);
    isInitialMount.current = false;
  }, []);

  // =====================================================
  // REFETCH WHEN PAGE CHANGES (only the next page's cases are loaded)
  // =====================================================
  useEffect(() => {
    if (isInitialMount.current) return;
    fetchFilteredCases(filters, currentPage);
  }, [currentPage]);

  // =====================================================
  // FILTER HANDLERS
  // =====================================================
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setShowFilterModal(false);
    setCurrentPage(1);
    fetchFilteredCases(filters, 1);
    showSuccessAlert('Filters Applied', 'Your filters have been applied successfully!');
  };

  const handleResetFilters = () => {
    const resetFilters = {
      filterType: 'account', filterValue: '', by: 'month', caseTag: 'any',
      properties: 'all', startDate: '', endDate: '', caseStatus: ''
    };
    setFilters(resetFilters);
    setCurrentPage(1);
    fetchFilteredCases(resetFilters, 1);
  };

  const getCurrentFilterOptions = () => {
    switch (filters.filterType) {
      case 'account': return filterOptions.accounts;
      case 'provider': return filterOptions.providers;
      case 'patient': return filterOptions.patients;
      case 'caseId': return filterOptions.caseIds;
      case 'delivery': return filterOptions.deliveryDates;
      default: return [];
    }
  };

  // =====================================================
  // CSV EXPORT
  // ✅ Exports ALL filtered open cases (not just the current page)
  // ✅ Only non-invoiced cases; ✅ Total Amount blank when no pricing access
  // =====================================================
  const handleExportCSV = async () => {
    try {
      const userLabId = getUserLabId();
      if (!userLabId) {
        showErrorAlert('Error', 'User Lab ID not found.');
        return;
      }

      const now = new Date();
      const params = {
        userLabId,
        statusFilter: 'open',
        fetchAllFiltered: 'true',
        filterType: filters.filterType,
        filterValue: filters.filterValue,
        by: filters.by,
        caseTag: filters.caseTag,
        properties: filters.properties,
        startDate: filters.startDate,
        endDate: filters.endDate,
        caseStatus: filters.caseStatus,
        currentMonth: now.getMonth(),
        currentYear: now.getFullYear(),
        ...(accountId && { accountId })
      };

      const response = await axios.get(`${API_URL}/api/cases/lab-cases`, { params });

      // ✅ Only export non-invoiced cases
      const exportCases = onlyNonInvoicedCases(response.data.data || []);

      if (exportCases.length === 0) {
        showErrorAlert('No Data', 'No cases to export!');
        return;
      }

      const headers = ['Case Number', 'Account', 'Provider', 'Patient', 'Status', 'Total Amount', 'Delivery Date', 'Start Date', 'Arrive Date', 'Color', 'Procedures'];

      const rows = exportCases.map(caseItem => {
        const procedures = caseItem.procedures?.map(p => p.procedure).join('; ') || 'N/A';
        return [
          caseItem.caseNumber || 'N/A',
          caseItem.account_name || caseItem.account_holder || 'N/A',
          caseItem.provider_name || 'N/A',
          caseItem.last_name || 'N/A',
          caseItem.status || 'N/A',
          // ✅ Blank when no pricing access
          hasPricingAccess ? (caseItem.totalPrice || 0) : '',
          caseItem.delivery_date ? new Date(caseItem.delivery_date).toLocaleDateString() : 'N/A',
          caseItem.start_date ? new Date(caseItem.start_date).toLocaleDateString() : 'N/A',
          caseItem.arrive_date ? new Date(caseItem.arrive_date).toLocaleDateString() : 'N/A',
          caseItem.color || 'N/A',
          procedures
        ];
      });

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => {
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const monthName = now.toLocaleString('default', { month: 'long' });
      link.setAttribute('href', url);
      link.setAttribute('download', `open_cases_${monthName}_${now.getFullYear()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessAlert('Export Successful', `Successfully exported ${exportCases.length} cases!`);
    } catch (error) {
      console.error('CSV Export Error:', error);
      showErrorAlert('Export Failed', 'Failed to export data.');
    }
  };

  // =====================================================
  // DROPDOWN
  // =====================================================
  const toggleDropdown = (caseId, event) => {
    event?.stopPropagation();
    setDropdownVisible(dropdownVisible === caseId ? null : caseId);
  };

  useEffect(() => {
    const handleClickOutside = event => {
      let isOutside = true;
      Object.keys(dropdownRef.current).forEach(key => {
        if (dropdownRef.current[key] && dropdownRef.current[key].contains(event.target)) {
          isOutside = false;
        }
      });
      if (isOutside) setDropdownVisible(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAccountClick = (accountName) => {
    const account = accounts.find(acc => acc.name === accountName);
    if (account) {
      navigate(`/tabsWorking/${account.accountId}`, { state: { accountData: account } });
    }
  };

  // =====================================================
  // PAGINATION
  // =====================================================
  const handleFirstPage = () => setCurrentPage(1);
  const handlePreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long' });
  const currentYearValue = now.getFullYear();

  // Loading & Error
  if (loading || pricingLoading) {
    return (
      <>
        <Header /><Navvbar />
        <div className="loading-container">
          <p>Loading open cases...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header /><Navvbar />
        <div className="error-container">
          <p className="error-text">{error}</p>
          <Button onClick={() => fetchFilteredCases(filters, 1)}>Retry</Button>
        </div>
      </>
    );
  }

  const displayCases = sortByColumn(cases, sortConfig);

  return (
    <>
      <Header />
      <Navvbar />

      <div className="cases-page cases-open-page" style={{ position: 'relative' }}>
        {/* Page Header */}
        <div className="cases-header">
          <h2 className="cases-title">
            {accountName
              ? `Open Cases for ${accountName}`
              : hasPricingAccess
                ? `Cases Opened ${monthName} ${currentYearValue} (${formatAmountWithCommas(monthlyTotal)})`
                : `Cases Opened ${monthName} ${currentYearValue}`}
          </h2>
        </div>

        {/* CSV and Filter Buttons */}
        <div className="action-buttons-wrapper">
          <button className="action-button" onClick={handleExportCSV} disabled={cases.length === 0}>
            <FiDownload /> CSV
          </button>
          <button className="action-button" onClick={() => setShowFilterModal(true)}>
            <LuFilter /> Filters
          </button>
        </div>

        {/* Loading Overlay */}
        {filterLoading && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(255,255,255,0.85)', display: 'flex',
            flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
            zIndex: 100, minHeight: '300px'
          }}>
            <div style={{
              width: '40px', height: '40px', border: '4px solid #f3f3f3',
              borderTop: '4px solid #004aad', borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '10px' }}>Loading cases...</p>
          </div>
        )}

        {/* Table */}
        <div className="cases-table-wrapper">
          <table className="cases-table">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '4%' }} />
              <col style={{ width: '21%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
            </colgroup>

            <thead>
              <tr>
                <SortHeader
                  label={
                    <span className="icon-tooltip" data-tooltip="Case Status">
                      <FaBox className="table-icon header-box-icon icon-inbox" />
                    </span>
                  }
                  colKey="status"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th><GrAttachment className="table-icon icon-attachment" /></th>
                <SortHeader label="Case/Pan" colKey="caseNumber" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Invoice" colKey="invoice" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account" colKey="account" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Provider" colKey="provider" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Patient/Appt" colKey="patient" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Account Ship" colKey="accountShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Lab Ship" colKey="labShip" sortConfig={sortConfig} onSort={handleSort} />
                <SortHeader label="Delivery Date" colKey="delivery" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {displayCases.length > 0 ? (
                displayCases.map(caseItem => {
                  const statusInfo = getStatusIcon(caseItem);
                  const StatusIcon = statusInfo.Icon;

                  return (
                    <tr key={caseItem._id} className="case-row">
                      <td className="table-cell status-cell">
                        <span className="icon-tooltip" data-tooltip={statusInfo.tip}>
                          <StatusIcon className={statusInfo.className} />
                        </span>
                      </td>
                      <td className="table-cell">
                        <GrAttachment className="table-icon icon-attachment" />
                      </td>
                      <td className="case-cell">
                        <div className="case-top-row">
                          <Link to={`/case/${caseItem.caseNumber}`} className="case-link">
                            #{caseItem.caseNumber}
                          </Link>
                          <button
                            ref={el => dropdownRef.current[caseItem._id] = el}
                            className="settings-button"
                            onClick={(e) => toggleDropdown(caseItem._id, e)}
                          >
                            <IoSettingsOutline className="settings-icon" />
                          </button>

                          {dropdownVisible === caseItem._id && (
                            <div className="case-dropdown">
                              <div className="dropdown-item">
                                <BsPencilFill size={14} /> Add Notes
                              </div>
                              <div className="dropdown-item dropdown-item-last">
                                <AiOutlineFileAdd size={14} /> Upload Files
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="pan-color">
                          <span>Pan Color:</span>
                          <span
                            className="pan-color-box"
                            style={{ backgroundColor: caseItem.color ? caseItem.color.toLowerCase() : '#ccc' }}
                          />
                          <span>{caseItem.color || 'N/A'}</span>
                        </div>

                        {caseItem.procedures && caseItem.procedures.length > 0 && (
                          <div className="procedures">
                            {caseItem.procedures.slice(0, 2).map((proc, idx) => (
                              <div key={idx}>{proc.procedure}</div>
                            ))}
                            {caseItem.procedures.length > 2 && (
                              <div>+{caseItem.procedures.length - 2} more</div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="invoice-cell">
                        {/* Since we only show non-invoiced cases, this will normally be empty.
                            Kept identical to original so nothing breaks if a value ever appears. */}
                        {caseItem.invoiceNumber ? (
                          <Link to={`/invoices/${formatInvoiceNumber(caseItem.invoiceNumber)}`} className="invoice-link">
                            #{formatInvoiceNumber(caseItem.invoiceNumber)}
                          </Link>
                        ) : ''}
                        {/* Amount shown only when pricing access is granted */}
                        {hasPricingAccess && (
                          <div className="invoice-total">
                            {caseItem.totalPrice && typeof caseItem.totalPrice === 'number'
                              ? formatAmountWithCommas(caseItem.totalPrice)
                              : null}
                          </div>
                        )}
                      </td>

                      <td className="table-cell account-cell">
                        <span
                          onClick={() => handleAccountClick(caseItem.account_name || caseItem.account_holder)}
                          className="account-link"
                        >
                          {caseItem.account_name || caseItem.account_holder || 'N/A'}
                        </span>
                      </td>

                      <td className="table-cell">{caseItem.provider_name || 'N/A'}</td>
                      <td className="table-cell">{caseItem.last_name || 'N/A'}</td>
                      <td className="table-cell">{formatShortDate(caseItem.start_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.arrive_date)}</td>
                      <td className="table-cell">{formatShortDate(caseItem.delivery_date)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="10" className="no-cases">
                    No open cases match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(cases.length > 0 || currentPage > 1 || totalPages > 1) && (
          <div className="pagination-container">
            <Button className="pagination-button" onClick={handleFirstPage} disabled={currentPage === 1}>First</Button>
            <Button className="pagination-button" onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</Button>
            {getPageNumbers().map(number => (
              <Button
                key={number}
                className={`pagination-button ${currentPage === number ? 'active' : 'outline'}`}
                onClick={() => setCurrentPage(number)}
              >
                {number}
              </Button>
            ))}
            <Button className="pagination-button outline" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</Button>
            <Button className="pagination-button outline" onClick={handleLastPage} disabled={currentPage === totalPages}>Last</Button>

          </div>
        )}
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} size="lg" centered>
        <Modal.Header className="modal-header-custom">
          <Modal.Title className="modal-title-custom">Change Filters</Modal.Title>
          <button onClick={() => setShowFilterModal(false)} className="modal-close-btn">
            <LuX />
          </button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="filter-modal-body">
              <div className="filter-section">
                <h5 className="filter-section-title">Filters</h5>

                <Form.Group controlId="filterType" className="filter-form-group">
                  <Form.Label className="filter-label">Filter By</Form.Label>
                  <Form.Select name="filterType" value={filters.filterType} onChange={handleFilterChange} className="filter-select">
                    <option value="account">Account</option>
                    <option value="provider">Provider</option>
                    <option value="patient">Patient</option>
                    <option value="caseId">Case ID</option>
                    <option value="delivery">Delivery Date</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterValue" className="filter-form-group">
                  <Form.Label className="filter-label">
                    {filters.filterType === 'account' ? 'Account' :
                     filters.filterType === 'provider' ? 'Provider' :
                     filters.filterType === 'patient' ? 'Patient' :
                     filters.filterType === 'caseId' ? 'Case ID' : 'Delivery Date'}
                  </Form.Label>
                  <Form.Select name="filterValue" value={filters.filterValue} onChange={handleFilterChange} className="filter-select">
                    <option value="">All</option>
                    {getCurrentFilterOptions().map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterBy" className="filter-form-group">
                  <Form.Label className="filter-label">Date Range</Form.Label>
                  <Form.Select name="by" value={filters.by} onChange={handleFilterChange} className="filter-select">
                    <option value="month">Current Month</option>
                    <option value="dateRange">Custom Date Range</option>
                  </Form.Select>
                </Form.Group>

                {filters.by === 'dateRange' && (
                  <>
                    <Form.Group controlId="filterStartDate" className="filter-form-group">
                      <Form.Label className="filter-label">Start Date</Form.Label>
                      <Form.Control type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="filter-input" />
                    </Form.Group>
                    <Form.Group controlId="filterEndDate" className="filter-form-group">
                      <Form.Label className="filter-label">End Date</Form.Label>
                      <Form.Control type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="filter-input" />
                    </Form.Group>
                  </>
                )}
              </div>

              <div className="filter-section">
                <h5 className="filter-section-title">Case Status Options</h5>

                <Form.Group controlId="filterCaseTag" className="filter-form-group">
                  <Form.Label className="filter-label">Case Tag</Form.Label>
                  <Form.Select name="caseTag" value={filters.caseTag} onChange={handleFilterChange} className="filter-select">
                    <option value="any">Any</option>
                    {filterOptions.caseTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterCaseStatus" className="filter-form-group">
                  <Form.Label className="filter-label">Case Status</Form.Label>
                  <Form.Select name="caseStatus" value={filters.caseStatus} onChange={handleFilterChange} className="filter-select">
                    <option value="">All Statuses</option>
                    {filterOptions.statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="filterProperties" className="filter-form-group">
                  <Form.Label className="filter-label">Properties</Form.Label>
                  <Form.Select name="properties" value={filters.properties} onChange={handleFilterChange} className="filter-select">
                    <option value="all">All</option>
                    <option value="digital">Digital Manufacturing Files</option>
                    <option value="enclosures">Enclosures</option>
                    <option value="both">Digital + Enclosures</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer-custom">
          <Button variant="secondary" onClick={handleResetFilters} className="reset-btn">Reset</Button>
          <Button variant="primary" onClick={handleApplyFilters} className="apply-btn">Apply</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default OpenCases;